import { add, format, isAfter, set } from 'date-fns';
import { getEinkaufsartAnzeige } from '@/core/common/helpers/utils.js';

// Setzt eine Uhrzeit im Format hh:mm auf ein Datum und addiert die Rollover-Tage
function parseZeitpunkt(datum, uhrzeit, rolloverTage = 0) {
  if (!datum || !uhrzeit) return null;

  const time = { hours: uhrzeit.substring(0, 2), minutes: uhrzeit.substring(3, 5) };

  return add(set(new Date(datum), time), { days: rolloverTage });
}

// Formatiert einen Zeitpunkt für die Anzeige
function formatZeitpunkt(date) {
  if (!date) return '-';

  return format(date, 'dd.MM.yyyy HH:mm');
}

// Listet die IATA-Codes der übergebenen Flughäfen kommasepariert auf
function formatFlughaefen(flughaefen) {
  if (!flughaefen) return '-';

  return flughaefen.map(fh => fh.flughafenIataCode).join(', ');
}

// Airline-Kuerzel aus der Flugnr GRP Hin des aeltesten Gruppeneinkaufs
function getAeltesteGruppeAirlineCode(reisetermin) {
  const gruppen = reisetermin?.flugeinkauf?.gruppeneinkaeufe;
  if (!gruppen || gruppen.length === 0) return '';

  const aelteste = gruppen[gruppen.length - 1];

  return aelteste?.flugnummerGruppeHin?.substring(0, 2) ?? '';
}

// Ab 2026 gilt das GRP-Konzept, davor die alten Enum-Werte
function isAfter2025(reisetermin) {
  if (!reisetermin) return false;

  return isAfter(new Date(reisetermin.startdatum), new Date('2025-12-31'));
}

// Liefert das Anzeige-Label der Einkaufsart
export function getEinkaufsartLabel(reisetermin) {
  if (!reisetermin || !reisetermin.flugeinkauf) {
    return 'EP';
  }

  const { einkaufsart, statusgruppe } = reisetermin.flugeinkauf;
  if (isAfter2025(reisetermin)) {
    return getEinkaufsartAnzeige(einkaufsart, statusgruppe, getAeltesteGruppeAirlineCode(reisetermin));
  }

  // Bis 2025 bleiben die alten Enum-Werte maßgeblich für die Anzeige
  const resGrpEnums = ['RestriktivBestaetigtBuchbar', 'LeerverkaufRes', 'RestriktivStattBackup'];
  const flexGrpEpEnums = ['FlexBuchbar', 'LeerverkaufFlex'];

  return resGrpEnums.includes(einkaufsart)
    ? 'RES GRP'
    : flexGrpEpEnums.includes(einkaufsart)
    ? 'FLEX GRP / EP'
    : 'EP';
}

// Liefert die Werte eines Feldes über alle Gruppeneinkaeufe hinweg untereinander
export function joinGruppeneinkaufWerte(reisetermin, key) {
  const gruppeneinkaeufe = reisetermin?.flugeinkauf?.gruppeneinkaeufe ?? [];
  if (gruppeneinkaeufe.length === 0) return '-';

  return gruppeneinkaeufe.map(gruppe => gruppe[key] ?? '-').join('<br>');
}

// Liefert je Gruppeneinkauf den Flughafen mit der Flugnummer im Format FRA (LH123)
export function formatGruppeneinkaeufeMitFlugnummer(reisetermin, flugnummerKey) {
  const gruppeneinkaeufe = reisetermin?.flugeinkauf?.gruppeneinkaeufe;
  if (!gruppeneinkaeufe || !Array.isArray(gruppeneinkaeufe)) return '';

  return gruppeneinkaeufe
    .map(gruppe => `${gruppe.flughafenIataCode} (${gruppe[flugnummerKey] ?? '-'})`)
    .filter(Boolean)
    .join('<br>');
}

// Bündelt alle Anzeigewerte der Fluginformationen
export function buildFluginformationen(reisetermin) {
  const metadata = reisetermin?.metadata;

  const fruehesteAnkunftDate = parseZeitpunkt(reisetermin?.startdatum, metadata?.fruehesteAnkunft);
  const fruehesterAbflugDate = parseZeitpunkt(
    reisetermin?.hotelendUndRueckflugdatum,
    metadata?.fruehesterAbflug
  );
  const spaetesteAnkunftDate = parseZeitpunkt(
    reisetermin?.startdatum,
    metadata?.spaetesteAnkunft,
    metadata?.restriktionenHinRollover ?? 0
  );
  const spaetesterAbflugDate = parseZeitpunkt(
    reisetermin?.hotelendUndRueckflugdatum,
    metadata?.spaetesterAbflug,
    metadata?.restriktionenRueckRollover ?? 0
  );

  return {
    einkaufsart: getEinkaufsartLabel(reisetermin),
    ziellandZielflughaefen: formatFlughaefen(metadata?.ziellandZielflughaefen),
    ziellandAbflughaefen: formatFlughaefen(metadata?.ziellandAbflughaefen),
    transferszenarioHin: metadata?.transferszenarioHin || '-',
    transferszenarioRueck: metadata?.transferszenarioRueck || '-',
    vorverlaengerung: metadata ? metadata.vorverlaengerung : '',
    fruehesteAnkunftDate,
    fruehesterAbflugDate,
    spaetesteAnkunftDate,
    spaetesterAbflugDate,
    fruehesteAnkunft: formatZeitpunkt(fruehesteAnkunftDate),
    fruehesterAbflug: formatZeitpunkt(fruehesterAbflugDate),
    spaetesteAnkunft: formatZeitpunkt(spaetesteAnkunftDate),
    spaetesterAbflug: formatZeitpunkt(spaetesterAbflugDate),
  };
}

// Liefert die in Reisefinder und Aufpreisrechner identischen Tabellenzeilen
export function buildFluginformationenZeilen(werte) {
  return [
    [
      { string: '', style: '', class: '' },
      { string: 'Hinflug', style: 'font-weight: bold', class: '' },
      { string: 'Rückflug', style: 'font-weight: bold', class: '' },
    ],
    [
      { string: 'ZL Zielflughafen / Abflughafen', style: '', class: 'row-description' },
      { string: werte.ziellandZielflughaefen, style: '', class: '' },
      { string: werte.ziellandAbflughaefen, style: '', class: '' },
    ],
    [
      { string: 'Früh. Ankunft / Abflug', style: '', class: 'row-description' },
      { string: werte.fruehesteAnkunft, style: '', class: '' },
      { string: werte.fruehesterAbflug, style: '', class: '' },
    ],
    [
      { string: 'Spät. Ankunft / Abflug', style: '', class: 'row-description' },
      { string: werte.spaetesteAnkunft, style: '', class: '' },
      { string: werte.spaetesterAbflug, style: '', class: '' },
    ],
    [
      { string: 'TF Szenario', style: '', class: 'row-description' },
      { string: werte.transferszenarioHin, style: '', class: '' },
      { string: werte.transferszenarioRueck, style: '', class: '' },
    ],
  ];
}
